import * as React from "react";
import { NavMenuOption, PageLayout } from "../../../components/PageLayout/PageLayout";
import { Typography, Grid } from "@mui/material";
import { BreadcrumbItem } from "../../../components/Breadcrumbs/Breadcrumbs";
import { Filter } from "./Filter/Filter";
import { ConnectedLinearChart } from "../../../components/charts/ConnectedLinearChart/ConnectedLinearChart";
import { ChartDataPoint } from "../../../components/charts/ChartDataPoint";
import { EscalationDetailsTable } from "./EscalationDetailsTable/EscalationDetailsTable";

const MT_DOC_ESCALATION_TO_HUMAN_EVENT_TYPE = "MT_DOC_ESCALATION_TO_HUMAN";
const MT_MEDIA_ESCALATION_TO_HUMAN_EVENT_TYPE = "MT_MEDIA_ESCALATION_TO_HUMAN";

const buildLinearChartQuery = (
  fromISO: string,
  toISO: string,
  openSearchFilters: Record<string, any>[],
  interval: string
): Record<string, any> => {
  const useCalendarInterval = interval === "1w" || interval === "1M";
  return {
    size: 0,
    query: {
      bool: {
        filter: [...openSearchFilters, { range: { "@timestamp": { gte: fromISO, lt: toISO } } }]
      }
    },
    aggs: {
      events_over_time: {
        date_histogram: {
          field: "@timestamp",
          ...(useCalendarInterval ? { calendar_interval: interval } : { fixed_interval: interval }),
          min_doc_count: 0,
          extended_bounds: { min: fromISO, max: toISO },
          time_zone: "America/Los_Angeles"
        }
      }
    }
  };
};

const parseLinearChartResponse = (response: any): ChartDataPoint[] => {
  const buckets = response?.aggregations?.events_over_time?.buckets ?? [];
  return buckets.map((bucket: any) => ({
    timestamp: bucket.key_as_string || new Date(bucket.key).toISOString(),
    count: bucket.doc_count as number
  }));
};

const breadcrumbs: BreadcrumbItem[] = [
  {
    path: "/analytics",
    label: "Analytics"
  },
  {
    path: "/analytics/mtEscalationAnalytics",
    label: "Machine Translation Escalations"
  }
];

const MtEscalationAnalyticsPage: React.FC = () => {
  const now = new Date();
  const [from, setFrom] = React.useState<Date>(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
  const [to, setTo] = React.useState<Date>(now);
  const docChartRefetchRef = React.useRef<() => void>(() => {});
  const mediaChartRefetchRef = React.useRef<() => void>(() => {});
  const tableRefetchRef = React.useRef<() => void>(() => {});

  const onFilterChange = React.useCallback((newFrom: Date, newTo: Date) => {
    setFrom(newFrom);
    setTo(newTo);
  }, []);

  const onRefresh = React.useCallback(() => {
    if (docChartRefetchRef.current) {
      docChartRefetchRef.current();
    }
    if (mediaChartRefetchRef.current) {
      mediaChartRefetchRef.current();
    }
    if (tableRefetchRef.current) {
      tableRefetchRef.current();
    }
  }, []);

  const fromISO = from.toISOString();
  const toISO = to.toISOString();

  const docChartBuildQuery = React.useCallback(
    (interval: string) =>
      buildLinearChartQuery(fromISO, toISO, [{ term: { eventType: MT_DOC_ESCALATION_TO_HUMAN_EVENT_TYPE } }], interval),
    [fromISO, toISO]
  );

  const mediaChartBuildQuery = React.useCallback(
    (interval: string) =>
      buildLinearChartQuery(
        fromISO,
        toISO,
        [{ term: { eventType: MT_MEDIA_ESCALATION_TO_HUMAN_EVENT_TYPE } }],
        interval
      ),
    [fromISO, toISO]
  );

  return (
    <PageLayout
      selectedNavMenu={NavMenuOption.ANALYTICS}
      title={
        <Typography variant="h6" sx={{ mb: 2 }}>
          Machine Translation Escalations
        </Typography>
      }
      breadcrumbs={breadcrumbs}
    >
      <Filter from={from} to={to} onChange={onFilterChange} onRefresh={onRefresh} />

      <Grid container={true} spacing={2}>
        <Grid item={true} xs={12} sm={6}>
          <ConnectedLinearChart
            id="mt-doc-escalations"
            title="Document Escalations Over Time"
            legend="Escalations"
            from={from}
            to={to}
            buildQuery={docChartBuildQuery}
            parseResponse={parseLinearChartResponse}
            refetchRef={docChartRefetchRef}
          />
        </Grid>
        <Grid item={true} xs={12} sm={6}>
          <ConnectedLinearChart
            id="mt-media-escalations"
            title="Media Escalations Over Time"
            legend="Escalations"
            from={from}
            to={to}
            buildQuery={mediaChartBuildQuery}
            parseResponse={parseLinearChartResponse}
            refetchRef={mediaChartRefetchRef}
          />
        </Grid>
      </Grid>

      <EscalationDetailsTable from={from} to={to} refetchRef={tableRefetchRef} />
    </PageLayout>
  );
};

export { MtEscalationAnalyticsPage };
